import { useState, useEffect } from "react";
import {
    TextField,
    Button,
    Card,
    CardContent,
    Alert,
    Container,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tooltip
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { APP_IMAGES } from "../../constants/AppImages";
import { APP_COLORS } from "../../theme/appTheme";
import { getBaseURL } from "../../build_config";
import { intl } from "dmsbasereact";

export const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validatingToken, setValidatingToken] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    // Password validation rules
    const passwordRules = {
        minLength: password.length >= 8,
        hasUppercase: /[A-Z]/.test(password),
        hasLowercase: /[a-z]/.test(password),
        hasDigit: /\d/.test(password),
        hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const isPasswordValid = Object.values(passwordRules).every(Boolean);
    const passwordsMatch = password === confirmPassword && password !== "";

    useEffect(() => {
        if (!token) {
            setError(intl.get("ResetPassword.NoTokenProvided").d("No reset token provided"));
            setValidatingToken(false);
            return;
        }

        // Validate token
        const validateToken = async () => {
            try {
                const response = await fetch(
                    `${getBaseURL()}/password-recovery/validate-token/${token}`
                );
                const data = await response.json();

                if (data.data?.valid) {
                    setTokenValid(true);
                } else {
                    setError(intl.get("ResetPassword.InvalidOrExpiredLink").d("This password reset link is invalid or has expired"));
                }
            } catch (err) {
                setError(intl.get("ResetPassword.UnableToValidateToken").d("Unable to validate reset token. Please try again later."));
            } finally {
                setValidatingToken(false);
            }
        };

        validateToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isPasswordValid) {
            setError(intl.get("ResetPassword.EnsurePasswordMeetsRequirements").d("Please ensure your password meets all requirements"));
            return;
        }

        if (!passwordsMatch) {
            setError(intl.get("ResetPassword.PasswordsDoNotMatch").d("Passwords do not match"));
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await fetch(`${getBaseURL()}/password-recovery/reset`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    token: token,
                    new_password: password,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(true);
                // Clear any existing session data to prevent password expiration warning
                sessionStorage.clear();
                localStorage.removeItem("refresh");
                setTimeout(() => {
                    navigate("/login");
                }, 3000);
            } else {
                setError(data.detail || intl.get("ResetPassword.ErrorOccurred").d("An error occurred. Please try again."));
            }
        } catch (err) {
            setError(intl.get("ResetPassword.UnableToConnect").d("Unable to connect to server. Please try again later."));
        } finally {
            setLoading(false);
        }
    };

    if (validatingToken) {
        return (
            <Container
                maxWidth="false"
                disableGutters
                sx={{
                    background: '#FFFFFF',
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                >
                    <CircularProgress sx={{ color: APP_COLORS.primary.main }} />
                    <Typography variant="body1" sx={{ mt: 2, color: APP_COLORS.neutral.gray900 }}>
                        {intl.get("ResetPassword.ValidatingToken").d("Validating reset token...")}
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (!tokenValid) {
        return (
            <Container
                maxWidth="false"
                disableGutters
                sx={{
                    background: '#FFFFFF',
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "100vh",
                        width: "100%",
                    }}
                >
                    <Card sx={{
                        maxWidth: "28rem",
                        width: "28rem",
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                        borderRadius: 3,
                    }}>
                        <CardContent>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error || intl.get("ResetPassword.InvalidOrExpiredLinkShort").d("Invalid or expired reset link")}
                            </Alert>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => navigate("/forgot-password")}
                            >
                                {intl.get("ResetPassword.RequestNewResetLink").d("Request New Reset Link")}
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Container>
        );
    }

    return (
        <Container
            maxWidth="false"
            disableGutters
            sx={{
                background: '#FFFFFF',
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                    width: "100%",
                    px: 2,
                }}
            >
                {/* Logo Section */}
                <Box
                    sx={{
                        mb: 4,
                        textAlign: "center",
                    }}
                >
                    <Box
                        component="img"
                        src={APP_IMAGES.LOGO}
                        alt="Lena Logo"
                        sx={{
                            width: "150px",
                            height: "auto",
                            mb: 2,
                            filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.3))",
                        }}
                    />
                </Box>

                <Card sx={{
                    maxWidth: "32rem",
                    width: "32rem",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                    borderRadius: 3,
                }}>
                    <CardContent>
                        <Box mb={3}>
                            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: APP_COLORS.neutral.gray900 }}>
                                {intl.get("ResetPassword.Title").d("Reset Your Password")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {intl.get("ResetPassword.Subtitle").d("Choose a strong password for your account")}
                            </Typography>
                        </Box>

                        {success ? (
                            <Alert severity="success">
                                {intl.get("ResetPassword.SuccessMessage").d("Password reset successfully! Redirecting to login...")}
                            </Alert>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                {error && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {error}
                                    </Alert>
                                )}

                                <Box mb={2}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="password"
                                        label={intl.get("NewPassword").d("New Password")}
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        variant="outlined"
                                        InputProps={{
                                            startAdornment: <LockIcon sx={{ mr: 1, color: APP_COLORS.neutral.gray600 }} />,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title={showPassword ? intl.get("Tooltip.Action.HidePassword").d("Ocultar senha") : intl.get("Tooltip.Action.ShowPassword").d("Mostrar senha")}>
                                                        <IconButton
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </Tooltip>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                <Box mb={2}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="confirmPassword"
                                        label={intl.get("ConfirmPassword").d("Confirm Password")}
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        disabled={loading}
                                        variant="outlined"
                                        error={confirmPassword !== "" && !passwordsMatch}
                                        helperText={
                                            confirmPassword !== "" && !passwordsMatch
                                                ? intl.get("ResetPassword.PasswordsDoNotMatch").d("Passwords do not match")
                                                : ""
                                        }
                                        InputProps={{
                                            startAdornment: <LockIcon sx={{ mr: 1, color: APP_COLORS.neutral.gray600 }} />,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title={showConfirmPassword ? intl.get("Tooltip.Action.HidePassword").d("Ocultar senha") : intl.get("Tooltip.Action.ShowPassword").d("Mostrar senha")}>
                                                        <IconButton
                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                            edge="end"
                                                        >
                                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </Tooltip>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                <Box mb={3} sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
                                        {intl.get("ResetPassword.Requirements").d("Password Requirements:")}
                                    </Typography>
                                    <List dense>
                                        <ListItem disablePadding>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                {passwordRules.minLength ? (
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                ) : (
                                                    <CancelIcon color="error" fontSize="small" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={intl.get("ResetPassword.RequirementMinLength").d("At least 8 characters")}
                                                primaryTypographyProps={{ variant: "body2" }}
                                            />
                                        </ListItem>
                                        <ListItem disablePadding>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                {passwordRules.hasUppercase ? (
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                ) : (
                                                    <CancelIcon color="error" fontSize="small" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={intl.get("ResetPassword.RequirementUppercase").d("One uppercase letter")}
                                                primaryTypographyProps={{ variant: "body2" }}
                                            />
                                        </ListItem>
                                        <ListItem disablePadding>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                {passwordRules.hasLowercase ? (
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                ) : (
                                                    <CancelIcon color="error" fontSize="small" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={intl.get("ResetPassword.RequirementLowercase").d("One lowercase letter")}
                                                primaryTypographyProps={{ variant: "body2" }}
                                            />
                                        </ListItem>
                                        <ListItem disablePadding>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                {passwordRules.hasDigit ? (
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                ) : (
                                                    <CancelIcon color="error" fontSize="small" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={intl.get("ResetPassword.RequirementNumber").d("One number")}
                                                primaryTypographyProps={{ variant: "body2" }}
                                            />
                                        </ListItem>
                                        <ListItem disablePadding>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                {passwordRules.hasSpecialChar ? (
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                ) : (
                                                    <CancelIcon color="error" fontSize="small" />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={intl.get("ResetPassword.RequirementSpecialChar").d("One special character (!@#$%^&*)")}
                                                primaryTypographyProps={{ variant: "body2" }}
                                            />
                                        </ListItem>
                                    </List>
                                </Box>

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={loading || !isPasswordValid || !passwordsMatch}
                                    sx={{
                                        py: 1.5,
                                        backgroundColor: APP_COLORS.primary.main,
                                        color: '#FFFFFF',
                                        fontWeight: 600,
                                        '&:hover': {
                                            backgroundColor: APP_COLORS.primary.dark,
                                        },
                                    }}
                                >
                                    {loading ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        intl.get("ResetPassword").d("Reset Password")
                                    )}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </Container>
    );
};
